import {
  AutomationActionGoogleCalendarEventInvitationDenormalized,
  ExperienceAnchorType,
  IAutomationActionTemplateGoogleCalendarEventInvitation
} from '@dadohr/shared/domainTypes/experience';
import assertNever from 'assert-never';
import { useMemo } from 'react';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { LightMdToJsx } from '../../../../../../../services/formatter';
import { humanizeDuration } from '../../../../../../../services/time';
import { AnchorField } from '../../../../Anchors';
import { MinimalTaskBuilderContext } from '../../../TaskBuilder/types';
import { actionActorToString } from '../../shared';
import { ActionDefinition } from '../../types';
import {
  ActionSummaryBody,
  ActionSummaryContainer,
  ActionSummaryHeading
} from '../shared';

export const ActionSummaryGoogleCalendarEventInvitation = ({
  a,
  expTpl,
  def,
  anchorFields
}: {
  a:
    | IAutomationActionTemplateGoogleCalendarEventInvitation
    | AutomationActionGoogleCalendarEventInvitationDenormalized;
  expTpl: MinimalTaskBuilderContext;
  def: ActionDefinition;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { anchor } = a;
  const { anchor: tplAnchor } = expTpl;
  const anchorText = useMemo(() => {
    if (anchor.type === 'AFTER_AUTOMATION') {
      return `${
        anchor.lowerBoundary ? humanizeDuration(anchor.lowerBoundary) : 'right'
      } after this automation runs`;
    }
    if (anchor.type === 'AFTER_EXPERIENCE_START_DATE') {
      return `${
        anchor.lowerBoundary ? humanizeDuration(anchor.lowerBoundary) : 'right'
      } after this experience starts`;
    }
    if (anchor.type === 'BEFORE_MILESTONE') {
      const anchorDef = anchorFields.find((a) => a.field === anchor.userField);
      if (!anchorDef) {
        console.error('UNKNOWN ANCHOR DEF');
        return '???';
      }
      return `${
        anchor.lowerBoundary ? humanizeDuration(anchor.lowerBoundary) : 'right'
      } before the ${anchorDef.label(t)}`;
    }
    if (anchor.type === 'AFTER_MILESTONE') {
      const anchorDef = anchorFields.find(
        (a) =>
          a.field === anchor.userField ||
          (tplAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
            tplAnchor.field) ||
          ''
      );
      if (!anchorDef) {
        console.error('UNKNOWN ANCHOR DEF');
        return '???';
      }
      return `${
        anchor.lowerBoundary ? humanizeDuration(anchor.lowerBoundary) : 'right'
      } after the ${anchorDef.label(t)}`;
    }
    return assertNever(anchor.type);
  }, [anchor, tplAnchor, anchorFields, t]);
  return (
    <ActionSummaryContainer>
      <ActionSummaryHeading def={def} />
      <ActionSummaryBody>
        <LightMdToJsx>
          {t(
            'Add **{{ participants }}** to {{ eventTitle }} in {{ calendarName }}. Find the first event **{{ timing }}**.',
            {
              participants:
                joinWithCommaAndAnd(
                  a.pplToAdd.map((o) =>
                    actionActorToString(o, expTpl.supportingActors, t)
                  ),
                  t
                ) || 'someone',
              eventTitle: a.query
                ? `an event called **${a.query}**`
                : '**an event**',
              calendarName: a.calendar.name
                ? `the calendar **${a.calendar.name}**`
                : '**a calendar**',
              timing: anchorText
            }
          )}
        </LightMdToJsx>
      </ActionSummaryBody>
    </ActionSummaryContainer>
  );
};
